import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import moment, { Moment } from "moment-timezone";

import * as advanceBookingRequestService from "../../../services/advanceBookingRequestService";
import useTablePagination from "../../../hooks/useTablePagination";
import { CustomNotification } from "../../../util/CustomNotification";
import { DefaultPagination } from "../../../constants/generalConstants";
import { ErrorMessageConstants } from "../../../constants/MessageConstants/ErrorMessageConstants";
import useValue from "../../../hooks/useValue";

import type { FormattedAdvanceBookingPenaltyRecord } from "../../../shared/interface/ABRReport.interface";
import type { RootState } from "../../../appRedux/store";

export const useABRPenaltyCollectionReport = () => {
  /* CONSTANT */
  /* Default last 30 selected */
  const days = 30;
  const to = moment();
  const from = moment().subtract(days, "days").startOf("day");

  /* STATE */
  const [abrList, setABRList] = useState<FormattedAdvanceBookingPenaltyRecord[]>([]);
  const [searchedText, setSearchedText] = useState("");
  const [selectedRecordId, setSelectedRecordId] = useState<number | null>(null);
  const [reFetch, setRefetch] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [totalPenaltyAmount, setTotalPenaltyAmount] = useState(0);
  const [startDate, setStartDate] = useState<string | null>(from.format("DD-MM-YYYY HH:mm"));
  const [endDate, setEndDate] = useState<string | null>(to.format("DD-MM-YYYY HH:mm"));

  /* HOOK */
  const { pagination, handleChangePagination, handleChangeTotalSize, handleResetPagination } = useTablePagination(0);
  const { masterFilters } = useSelector((state: RootState) => state.common);
  const { value: loading, handleEnableValue: startLoading, handleDisableValue: stopLoading } = useValue(false);

  /* HANDLERS */
  let startIndex = ((pagination?.current ?? 1) - 1) * (pagination?.pageSize ?? 6);

  const handleRefreshList = () => {
    setRefetch(true);
  };

  const handleChangeOnSearch = (value: string) => {
    const trimmedValue = value?.trimStart();
    setSearchedText(trimmedValue);
    handleResetPagination();
    if (!value) setRefetch(true);
  };

  const handleChangeSearch = (value: string) => {
    const trimmedValue = value?.trimStart();
    setSearchedText(trimmedValue);
    handleResetPagination();
    setRefetch(true);
  };

  const fetchABRList = async () => {
    try {
      if (!masterFilters?.customerId || !masterFilters?.locationId) {
        CustomNotification.showErrorMessage(ErrorMessageConstants.SELECT_CUSTOMER_LOCATION_FILTER);
        return;
      }

      startLoading();

      let params = {
        page: pagination?.current || DefaultPagination.PAGE,
        pageSize: pagination?.pageSize || DefaultPagination.PAGE_SIZE,
        customerId: masterFilters?.customerId,
        locationId: masterFilters?.locationId,
        fromDate: moment(startDate, "DD-MM-YYYY HH:mm").format("YYYY-MM-DD HH:mm"),
        toDate: moment(endDate, "DD-MM-YYYY HH:mm").format("YYYY-MM-DD HH:mm"),
        searchQuery: searchedText
      };

      const response = await advanceBookingRequestService.getABRPenaltyCollectionReport(params);

      const formatted = response?.data?.rows?.map((item, index: number) => ({
        ...item,
        parking_amount: item.amount.parking_amount,
        valet_parking_amount: item.amount.valet_parking_amount,
        penalty_amount: item.amount.penalty_amount,
        overdue_amount: item.amount.overdue_amount,
        payable_amount: item.amount.payable_amount,
        sr_no: startIndex + index + 1
      }));

      setTotalPenaltyAmount(response?.data?.totalPenaltyAmount ?? 0);

      handleChangeTotalSize(response?.data?.count || 0);
      setABRList(formatted);
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    } finally {
      setRefetch(false);
      stopLoading();
    }
  };

  const handleModalOpen = (recordId: number) => {
    setSelectedRecordId(recordId);
    setIsModalVisible(true);
  };

  const handleOnClose = () => {
    setIsModalVisible(false);
    setSelectedRecordId(null);
  };

  const handleGo = () => {
    setRefetch(true);
  };

  /* EFFECT */
  useEffect(() => {
    if (pagination?.current && pagination?.pageSize && masterFilters?.customerId && masterFilters?.locationId) {
      setRefetch(true);
    } else {
      setRefetch(false);
    }
  }, [pagination?.current, pagination?.pageSize, masterFilters?.customerId, masterFilters?.locationId]);

  useEffect(() => {
    fetchABRList();
  }, [reFetch]);

  return {
    abrList,
    loading,
    pagination,
    handleChangePagination,
    fetchABRList,
    handleRefreshList,
    isModalVisible,
    selectedRecordId,
    handleChangeSearch,
    searchedText,
    handleOnClose,
    handleModalOpen,
    handleChangeOnSearch,
    totalPenaltyAmount,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    handleGo
  };
};
